import React, { useEffect } from 'react';

import { get } from 'lodash';
import css from '../SetPlan.module.scss';
import Button from '../../../../components/UI/Button';
import { Plan } from '../index';
import BackButton from '../../../../components/UI/BackButton';
import { PlanInfo } from '../ChecklistsAndContent/content';
import LearnMoreDetails from './LearnMoreDetails';
import PlanTools from './PlanTools';
import { useToggles } from '../../../../state/toggles';

interface Props {
  plan: Plan;
  price: number;
  loading: boolean;
  flowId: string;
  onBackClicked: () => void;
  onUpgradeClicked: (plan: Plan) => void;
  onSkip: () => void;
  analytics: any;
  riskFreePaintedDoorEnabled?: boolean;
}

const PlanDetails = ({
  plan,
  price,
  onBackClicked,
  onUpgradeClicked,
  loading,
  analytics
}: Props) => {
  const { defaultInfinity, defaultMax, riskFreePaintedDoor } = useToggles();
  useEffect(() => {
    analytics.trackEvent('Registration Invest Plan Info Viewed', {
      eventProps: {
        plan,
        price,
        limitedInfo: false
      }
    });
  }, []);
  const planInfo = PlanInfo[plan];
  const displayLearnMoreDetailsAndFooter = planInfo.planName !== 'standard';
  const isMaxOrInfinityXP = defaultInfinity.enabled || defaultMax.enabled;

  return (
    <>
      <BackButton onClick={onBackClicked} />
      <div>
        <h1 data-e2e="invest-info-header">{planInfo.header}</h1>
        <h2 data-e2e="invest-info-subheader">{planInfo.subheader}</h2>
        <PlanTools plan={plan} />
        {displayLearnMoreDetailsAndFooter && <LearnMoreDetails plan={plan} />}
        <Button
          text={`${
            isMaxOrInfinityXP ? 'Continue with' : 'Upgrade to Greenlight'
          } ${planInfo.planName}`}
          loading={loading}
          onClick={() => {
            analytics.trackEvent('Registration Invest Plan Action', {
              eventProps: {
                plan,
                price,
                skipped: false
              }
            });
            onUpgradeClicked(plan);
          }}
          data-e2e={`upgrade-${plan}`}
        />
        <p
          className={`${css.investSecondaryButton} ${css.investSecondaryButtonNew}`}
          onClick={onBackClicked}
        >
          Go back
        </p>
        {displayLearnMoreDetailsAndFooter && (
          <div className={css.investFooter}>
            <>
              {
                riskFreePaintedDoor.enabled && (
                  <>
                    <p>
                      If you don't love Greenlight, simply request a 
                      refund within 30 days of starting your subscription — and get your 
                      money back.
                    </p>
                    <p>
                      ‡Valid for new customers only. Subject to identity 
                      verification and minimum load requirements. Your first monthly fee 
                      will be billed to your parent wallet seven days after successful 
                      registration. To receive a refund of your first monthly fee, you 
                      must request to close your account on or before the day immediately 
                      preceding your first Monthly Billing Date. See the ‘Account’ tab of 
                      Settings by tapping the gear icon on the Greenlight app home page to 
                      confirm when your risk-free trial ends. See{' '}
                      <a
                        href="https://greenlight.com/terms-of-service"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Terms
                      </a>{' '}for details.
                    </p>
                </>
                )
              }
              {plan === Plan.infinity && (
                <p>
                  &dagger;Requires mobile data or a WiFi connection, and access
                  to sensory and motion data from cell phone to utilize family
                  location sharing, SOS alerts and crash detection features.
                  Messaging and data rates and other terms may apply.
                </p>
              )}
              <p>
                ¹See Greenlight’s Terms of Service for details on earnings
                limits and how to qualify. Subject to change at any time.
              </p>
              {plan !== 'standard' && (
                <p>
                  ²Cell phone and purchase protection provided by Virginia
                  Surety Company Inc. Cell phone protection is not available to
                  residents of New York.
                </p>
              )}
            </>
          </div>
        )}
      </div>
    </>
  );
};

export default PlanDetails;
